import type { LayoutBlock, Run } from "./pdfLayout";

/** A page of the converted document, reduced to what the on-screen preview needs (no picture bytes). */
export interface PreviewPage {
  widthPt: number;
  heightPt: number;
  blocks: (LayoutBlock | { kind: "image"; widthPt: number; heightPt: number })[];
  note?: string;
}

function RunSpan({ run }: { run: Run }) {
  const style: React.CSSProperties = {
    fontWeight: run.bold ? 700 : undefined,
    fontStyle: run.italic ? "italic" : undefined,
    color: run.color ? `#${run.color}` : run.link ? "#0563C1" : undefined,
    textDecoration: run.link ? "underline" : undefined,
    fontSize: `${Math.max(7, Math.min(40, run.sizePt))}pt`,
  };
  return <span style={style}>{run.field ? (run.field === "PAGE" ? "1" : "N") : run.text.replace(/\t/g, "    ")}</span>;
}

function Runs({ runs }: { runs: Run[] }) {
  return (
    <>
      {runs.map((r, i) => (
        <RunSpan key={i} run={r} />
      ))}
    </>
  );
}

/**
 * Shows the first pages of what will be in the Word file (text, styling, tables, where pictures
 * go), so problems are visible before downloading. This is a quick look, not a pixel-exact render
 * of how Word will lay it out.
 */
export function ConvertedPreview({ pages }: { pages: PreviewPage[] }) {
  return (
    <div className="space-y-3" aria-label="Preview of the converted document">
      {pages.map((page, pi) => (
        <div key={pi} className="rounded-md border bg-white text-black p-4 overflow-x-auto">
          <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-2">Page {pi + 1}</p>
          {page.note && <p className="text-xs text-gray-600 italic">{page.note}</p>}
          {page.blocks.map((b, bi) => {
            if (b.kind === "paragraph") {
              return (
                <p
                  key={bi}
                  style={{ textAlign: b.align, marginLeft: b.indentPt ? Math.min(b.indentPt, 120) : undefined, marginBottom: Math.max(2, Math.min(14, b.spaceAfterPt)) }}
                  className={b.bullet ? "pl-5 relative" : undefined}
                >
                  {b.bullet && <span className="absolute left-1">•</span>}
                  <Runs runs={b.runs} />
                </p>
              );
            }
            if (b.kind === "table") {
              return (
                <table key={bi} className="border-collapse my-2 text-[10pt]">
                  <tbody>
                    {b.rows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td key={ci} className="border border-gray-300 px-1.5 py-0.5 align-top" style={{ textAlign: b.colAlign[ci] }}>
                            <Runs runs={cell.runs} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            }
            return (
              <div key={bi} className="my-2 flex items-center justify-center rounded bg-gray-100 text-xs text-gray-500" style={{ height: Math.min(160, Math.max(30, b.heightPt * 0.4)), maxWidth: Math.max(60, b.widthPt * 0.8) }}>
                picture
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
